<template>
  <div :class="['MapProposalCard', { 'is-mobile': isMobile, embedded }]">
    <div v-if="!embedded" class="location-label">
      <span class="label-text">{{ location.name || localize('gambit.proposal.new_spot') }}</span>
    </div>

    <div v-if="contextLabel" class="context-label">{{ contextLabel }}</div>

    <IntentDaySelect
      v-if="(!isEditMode || isEditChallenge) && availableIntents.length > 0"
      v-model="selectedIntentId"
      :intents="availableIntents"
      class="intent-day"
    />

    <div :class="['time-span']">
      <div class="hours">
        <GamButton
          :variant="GamButtonVariant.SECONDARY"
          :size="GamButtonSize.SMALL"
          @click="handlePreviousTime"
          :is-disabled="!isPreviousTimeAvailable"
          :left-icon="GamIconName.SMALL_ARROW_LEFT"
          is-icon-only
        />

        <div
          :class="['digits', 'time-display', { 'visual-left-align': timeDisplayValue?.[0] === '1' }]"
          :aria-disabled="!hasAvailableStartTimes"
        >
          {{ timeDisplayValue }}
        </div>

        <GamButton
          :variant="GamButtonVariant.SECONDARY"
          :size="GamButtonSize.SMALL"
          @click="handleNextTime"
          :is-disabled="!isNextTimeAvailable"
          :left-icon="GamIconName.SMALL_ARROW_RIGHT"
          is-icon-only
        />
      </div>
    </div>

    <div class="inline-options" @click="isOptionsOpen = !isOptionsOpen">
      <div class="option-pill" :class="{ active: isOptionsOpen || selectedTagLabels.length > 0 }">
        <span v-if="selectedTagLabels.length">{{ selectedTagLabels.join(', ') }}</span>
        <span v-else>Tags</span>
      </div>
      <div v-if="title" class="option-pill active">Title</div>
    </div>

    <Transition name="expand">
      <div v-if="isOptionsOpen" class="options-expanded">
        <GamSimpleInput v-model="title" placeholder="Title" class="inline-title-input" is-small />
        <GambitTypeSelector v-model="gambitType" :options="tagOptions" is-streaming-hidden class="inline-tags" />
      </div>
    </Transition>

    <ActionBar ref="actionBarRef" class="action-bar">
      <GamChessSetCounter ref="chessCounterRef" v-model="chessSetCount" />

      <div v-if="!isChallenge" class="player-circles">
        <div v-for="slot in visibleInvitees" :key="slot.id" class="player-circle">
          <PlayerAvatar :player="slot" is-invited is-overlapping hide-rating />
        </div>
        <div class="player-circle">
          <div class="circle-placeholder invite" @click="handleOpenInviteModal">
            <span v-if="selectedPlayers.length" class="invite-count">{{ selectedPlayers.length }}</span>
            <GamIcon v-else :name="GamIconName.PLUS" size="24px" is-current />
          </div>
        </div>
      </div>
    </ActionBar>

    <div class="bottom-ctas">
      <div class="proposal-cta-circle" @click="emit('close')">
        <GamIcon :name="GamIconName.X_CLOSE" :size="GamIconSize.X_LARGE" is-current />
      </div>
      <div
        :class="[
          'proposal-cta-circle',
          'proposal-cta-play',
          { 'is-breathing': selectedPlayers.length > 0, 'is-disabled': isSuggestDisabled || isSubmitting },
        ]"
        @click="!isSuggestDisabled && !isSubmitting && handleCreateProposal()"
      >
        <GamIcon :name="GamIconName.PLAY_EMPTY" :size="GamIconSize.X_LARGE" is-current />
      </div>
    </div>

    <ClosedSpotNoticeDialog
      v-if="isClosedSpotNoticeOpen"
      :opening-hours="closedSpotHours"
      :highlight-day="closedSpotHighlightDay"
      @create-anyways="createClosedSpotAnyways"
      @change="changeClosedSpot"
    />
  </div>
</template>

<script lang="ts" setup>
import { RouterNameType } from '@/core/data/config/uiConfig.interface';
import { GambitCategory, GambitChessSetPolicy, GambitType } from '@/core/data/gambit/gambit.type';
import type { MapLocationDto } from '@/core/data/location/location.interface';
import { LocationType } from '@/core/data/location/location.type';
import type { CreateProposalDto, SparkIntent } from '@/core/data/spark/spark.interface';
import { gambits, localize } from '@/core/gambits';
import type { OpeningHourEntry } from '@/core/helpers/opening-hours.helper';
import { isMobile } from '@/core/helpers/ui.helper';
import { useCityStore } from '@/stores/city.store';
import { useMapStore } from '@/stores/map.store';
import { useSparkStore } from '@/stores/spark.store';
import ActionBar from '@/views/components/ActionBar.vue';
import GamButton from '@/views/components/GamButton.vue';
import GamChessSetCounter from '@/views/components/GamChessSetCounter.vue';
import GamIcon from '@/views/components/GamIcon.vue';
import GamSimpleInput from '@/views/components/GamSimpleInput.vue';
import { GamButtonSize, GamButtonVariant } from '@/views/composables/constants/components/gamButton.constants';
import { GamIconName, GamIconSize } from '@/views/composables/constants/components/gamIcon.constants';
import { useClosedSpotNotice } from '@/views/composables/hooks/useClosedSpotNotice';
import type { GambitForm } from '@/views/composables/models/form.interface';
import GambitTypeSelector from '@/views/main/gambits/forms/GambitTypeSelector.vue';
import type { ChosenPlayer } from '@/views/main/gambits/modals/InvitePlayers.vue';
import InvitePlayers from '@/views/main/gambits/modals/InvitePlayers.vue';
import ClosedSpotNoticeDialog from '@/views/main/locations/modals/ClosedSpotNoticeDialog.vue';
import { handleCreateLocation } from '@/views/main/locations/side-panels/locationActions';
import IntentDaySelect from '@/views/main/sparks/components/IntentDaySelect.vue';
import PlayerAvatar from '@/views/main/sparks/components/PlayerAvatar.vue';
import ConfirmDialog from '@/views/main/sparks/modals/ConfirmDialog.vue';
import { addDays, addHours, addMinutes, format, setMilliseconds, setSeconds, subMinutes } from 'date-fns';
import { fromZonedTime, toZonedTime } from 'date-fns-tz';
import { storeToRefs } from 'pinia';
import { computed, onActivated, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useModal } from 'vue-final-modal';
import { useRouter } from 'vue-router';
import { useToast } from 'vue-toastification';

interface MapProposalCardProps {
  location: MapLocationDto;
  isChallenge?: boolean;
  challengeUserId?: string | null;
  defaultTitle?: string;
  embedded?: boolean;
  now?: Date;
  openingHours?: OpeningHourEntry[] | null;
}

const props = withDefaults(defineProps<MapProposalCardProps>(), {
  isChallenge: false,
  challengeUserId: null,
  defaultTitle: '',
  embedded: false,
  now: undefined,
  openingHours: undefined,
});

const router = useRouter();
const mapStore = useMapStore();
const {
  isOpen: isClosedSpotNoticeOpen,
  hours: closedSpotHours,
  highlightDay: closedSpotHighlightDay,
  confirmIfOutside: confirmClosedSpotIfOutside,
  createAnyways: createClosedSpotAnyways,
  change: changeClosedSpot,
} = useClosedSpotNotice();
const isVirtualLocation = computed(() => props.location.id === mapStore.VIRTUAL_LOCATION_ID);
const isEditMode = computed(() => !!mapStore.editGambitId);
const isEditChallenge = computed(() => isEditMode.value && mapStore.editGambitIsChallenge);

let pendingGambitConflictId: string | null = null;
const { open: openGambitExistsModal, close: closeGambitExistsModal } = useModal({
  component: ConfirmDialog,
  attrs: {
    title: localize('spark.gambit_exists.title'),
    prompt: localize('spark.gambit_exists.prompt'),
    confirmLabel: localize('spark.gambit_exists.view'),
    cancelLabel: localize('app.verb.cancel'),
    onConfirm: async () => {
      const gambitId = pendingGambitConflictId;
      pendingGambitConflictId = null;
      await closeGambitExistsModal();
      if (gambitId) {
        router.push({ name: RouterNameType.GAMBIT, params: { id: gambitId }, query: { back: 'history' } });
      }
    },
    onCancel: async () => {
      pendingGambitConflictId = null;
      await closeGambitExistsModal();
    },
  },
});

const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'created', locationId: string): void;
}>();

const resolvedVirtualLocationId = ref<string | null>(null);

const cityStore = useCityStore();
const { city } = storeToRefs(cityStore);
const sparkStore = useSparkStore();
const { intents } = storeToRefs(sparkStore);

const isSubmitting = ref(false);
const gambitType = ref<GambitType[]>([GambitType.ANY]);
const chosenStartTime = ref<Date>(new Date());
const editTimeInitialized = ref(false);
const userHasSetTime = ref(false);

const isOptionsOpen = ref(false);
const title = ref(props.defaultTitle || '');
const chessSetCount = ref(0);
const contextLabel = computed(() => {
  if (props.isChallenge && mapStore.challengeUsername) {
    return `with @${mapStore.challengeUsername}`;
  }
  return null;
});

const tagOptions = [
  { value: GambitType.FREESTYLE, label: 'gambit.detail.tag.category.freestyle' },
  { value: GambitType.BLITZ, label: 'gambit.detail.tag.category.blitz' },
  { value: GambitType.RAPID, label: 'gambit.detail.tag.category.rapid' },
  { value: GambitType.BULLET, label: 'gambit.detail.tag.category.bullet' },
  { value: GambitType.CLASSIC, label: 'gambit.detail.tag.category.classic' },
  { value: GambitType.BUGHOUSE, label: 'gambit.detail.tag.category.bughouse' },
  { value: GambitType.HAND_AND_BRAIN, label: 'gambit.detail.tag.category.hand_and_brain' },
  { value: GambitType.STIMUL, label: 'gambit.detail.tag.category.stimul' },
  { value: GambitType.WALK, label: 'gambit.detail.tag.category.walk' },
  { value: GambitType.SHOGI, label: 'gambit.detail.tag.category.shogi' },
  { value: GambitType.GO, label: 'gambit.detail.tag.category.go' },
  { value: GambitType.BACKGAMMON, label: 'gambit.detail.tag.category.backgammon' },
  { value: GambitType.CATAN, label: 'gambit.detail.tag.category.catan' },
  { value: GambitType.SCRABBLE, label: 'gambit.detail.tag.category.scrabble' },
  { value: GambitType.CARDS, label: 'gambit.detail.tag.category.cards' },
  { value: GambitType.BOARD_GAMES, label: 'gambit.detail.tag.category.board_games' },
  { value: GambitType.BEGINNER, label: 'gambit.detail.tag.category.beginner' },
  { value: GambitType.INTERMEDIATE, label: 'gambit.detail.tag.category.intermediate' },
  { value: GambitType.ADVANCED, label: 'gambit.detail.tag.category.advanced' },
  { value: GambitType.LADIES_ONLY, label: 'gambit.detail.tag.category.ladies_only' },
  { value: GambitType.MUSIC, label: 'gambit.detail.tag.category.music' },
  { value: GambitType.CURATED, label: 'gambit.detail.tag.category.curated' },
  { value: GambitType.DONATIONS, label: 'gambit.detail.tag.category.donations' },
];

const selectedTagLabels = computed(() => {
  if (!gambitType.value.length || (gambitType.value.length === 1 && gambitType.value[0] === GambitType.ANY)) {
    return [];
  }
  return gambitType.value.map((t) => localize(`gambit.detail.tag.category.${t}`));
});

const selectedPlayers = ref<ChosenPlayer[]>([]);
const MAX_INVITE_FACES = 3;
// Same size and gap as .circle-placeholder and .player-circles below.
const INVITE_CIRCLE_SIZE = 40;
const INVITE_CIRCLE_GAP = 8;

const actionBarRef = ref<{ $el?: unknown } | null>(null);
const chessCounterRef = ref<{ $el?: unknown } | null>(null);
const faceCapacity = ref(0);
let inviteResizeObserver: ResizeObserver | null = null;

function elementOf(target: { $el?: unknown } | null): HTMLElement | null {
  const el = target?.$el;
  return el instanceof HTMLElement ? el : null;
}

function measureInviteFaces() {
  const bar = elementOf(actionBarRef.value);
  const counter = elementOf(chessCounterRef.value);
  if (!bar || bar.clientWidth === 0) return;

  const barStyles = getComputedStyle(bar);
  const padding = parseFloat(barStyles.paddingLeft) + parseFloat(barStyles.paddingRight);
  const barGap = parseFloat(barStyles.columnGap || barStyles.gap) || 0;
  const available = bar.clientWidth - padding - (counter?.offsetWidth ?? 0) - barGap;
  const slot = INVITE_CIRCLE_SIZE + INVITE_CIRCLE_GAP;
  const circlesThatFit = Math.max(0, Math.floor((available + INVITE_CIRCLE_GAP) / slot));
  faceCapacity.value = Math.max(0, Math.min(MAX_INVITE_FACES, circlesThatFit - 1));
}

const visibleInvitees = computed(() =>
  selectedPlayers.value.slice(0, faceCapacity.value).map((player) => ({
    id: player.id,
    username: player.username,
    profileImageBlobHash: player.profileImageBlobHash ?? null,
  })),
);

function handleOpenInviteModal() {
  const { open, close } = useModal({
    component: InvitePlayers,
    attrs: {
      participantIds: [],
      initialPlayers: [...selectedPlayers.value],
      mode: 'select' as const,
      onSelect: (players: ChosenPlayer[]) => {
        selectedPlayers.value = players;
      },
      onClose: () => close(),
    },
  });
  open();
}

const CUSTOM_PROPOSAL_MIN_HOUR = 8;
const CUSTOM_PROPOSAL_MAX_START_HOUR = 22;
const TIME_ARROW_STEP_MINUTES = 30;
const INTENT_DAY_CUTOFF_HOUR = 21;

const fallbackNow = ref(new Date());
const clockNow = computed(() => props.now ?? fallbackNow.value);

onActivated(() => {
  fallbackNow.value = new Date();
});

const availableIntents = computed(() => {
  const now = clockNow.value;
  const todayKey = format(now, 'yyyy-MM-dd');
  const sevenDayEndKey = format(addDays(now, 6), 'yyyy-MM-dd');

  return intents.value
    .filter((intent) => {
      const intentDateKey = String(intent.date).substring(0, 10);
      if (intentDateKey < todayKey || intentDateKey > sevenDayEndKey) return false;
      if (intent.isDisabled) return false;

      const tz = intent.timeZone || 'UTC';
      const zonedNow = toZonedTime(now, tz);
      if (intentDateKey === format(zonedNow, 'yyyy-MM-dd') && zonedNow.getHours() >= INTENT_DAY_CUTOFF_HOUR) {
        return false;
      }

      return true;
    })
    .sort((a, b) => String(a.date || '').localeCompare(String(b.date || '')));
});

const selectedIntentId = ref<string>('');

function pickDefaultIntentId(): string {
  if (!availableIntents.value.length) return '';
  return availableIntents.value[0].id;
}

const selectedIntent = computed((): SparkIntent | null => {
  if (!selectedIntentId.value) return null;
  return availableIntents.value.find((i) => i.id === selectedIntentId.value) || null;
});

const effectiveTimeZone = computed(() => selectedIntent.value?.timeZone || city.value?.timezone || 'UTC');

const effectiveDateKey = computed(() => {
  if (selectedIntent.value) {
    return String(selectedIntent.value.date).substring(0, 10);
  }
  return format(toZonedTime(clockNow.value, effectiveTimeZone.value), 'yyyy-MM-dd');
});

const isEffectiveDateToday = computed(() => {
  const todayInTz = format(toZonedTime(clockNow.value, effectiveTimeZone.value), 'yyyy-MM-dd');
  return effectiveDateKey.value === todayInTz;
});

function getFloorHalfHourInTimeZone(now: Date, timeZone: string): Date {
  const zonedNow = toZonedTime(now, timeZone);
  const minuteRemainder = zonedNow.getMinutes() % TIME_ARROW_STEP_MINUTES;
  const flooredZonedNow = setSeconds(setMilliseconds(addMinutes(zonedNow, -minuteRemainder), 0), 0);

  try {
    return fromZonedTime(flooredZonedNow, timeZone);
  } catch {
    return new Date(now.getTime());
  }
}

function buildUtcDateFromWallTime(date: string, hours: number, timeZone: string): Date | null {
  const normalizedDate = String(date).substring(0, 10);
  const normalizedHours = String(hours).padStart(2, '0');
  const candidate = new Date(`${normalizedDate}T${normalizedHours}:00:00`);

  if (Number.isNaN(candidate.getTime())) {
    return null;
  }

  try {
    return fromZonedTime(candidate, timeZone);
  } catch {
    return null;
  }
}

const startTimeRange = computed(() => {
  const now = clockNow.value;
  const tz = effectiveTimeZone.value;
  const dateKey = effectiveDateKey.value;

  const minStart = buildUtcDateFromWallTime(dateKey, CUSTOM_PROPOSAL_MIN_HOUR, tz) || now;
  const maxStart = buildUtcDateFromWallTime(dateKey, CUSTOM_PROPOSAL_MAX_START_HOUR, tz) || now;
  const floorNow = getFloorHalfHourInTimeZone(now, tz);
  const adjustedMinStart = isEffectiveDateToday.value && minStart.getTime() < floorNow.getTime() ? floorNow : minStart;

  return {
    min: adjustedMinStart,
    max: maxStart,
    hasAvailable: adjustedMinStart.getTime() <= maxStart.getTime(),
  };
});

const hasAvailableStartTimes = computed(() => startTimeRange.value.hasAvailable);

const clampStartTime = (candidateStartTime: Date): Date => {
  const { min, max, hasAvailable } = startTimeRange.value;
  if (!hasAvailable) return new Date(max.getTime());
  if (candidateStartTime.getTime() < min.getTime()) return new Date(min.getTime());
  if (candidateStartTime.getTime() > max.getTime()) return new Date(max.getTime());
  return new Date(candidateStartTime.getTime());
};

const getDefaultStartTime = (): Date => {
  const { min, max, hasAvailable } = startTimeRange.value;
  if (!hasAvailable) return new Date(max.getTime());

  if (isEffectiveDateToday.value) {
    return new Date(min.getTime());
  }

  const midpointTimestamp = min.getTime() + Math.floor((max.getTime() - min.getTime()) / 2);
  return clampStartTime(new Date(midpointTimestamp));
};

const zonedStartTime = computed(() => toZonedTime(chosenStartTime.value, effectiveTimeZone.value));
const timeDisplayValue = computed(() => format(zonedStartTime.value, 'HH:mm'));

const isPreviousTimeAvailable = computed(() => {
  if (!hasAvailableStartTimes.value) return false;
  return chosenStartTime.value.getTime() > startTimeRange.value.min.getTime();
});

const isNextTimeAvailable = computed(() => {
  if (!hasAvailableStartTimes.value) return false;
  return chosenStartTime.value.getTime() < startTimeRange.value.max.getTime();
});

const isSuggestDisabled = computed(() => {
  if (isEditMode.value) {
    return isSubmitting.value || !hasAvailableStartTimes.value;
  }
  return isSubmitting.value || !hasAvailableStartTimes.value || !city.value?.id;
});

const normalizeProposalGambitTypes = (types?: GambitType[]) => {
  if (!types?.length) return [GambitType.ANY];
  const normalized = types.filter((type) => ![GambitType.STREAMED, GambitType.TIMED].includes(type));
  return normalized.length ? normalized : [GambitType.ANY];
};

watch(
  () => getFloorHalfHourInTimeZone(clockNow.value, effectiveTimeZone.value).getTime(),
  () => {
    if (userHasSetTime.value || (isEditChallenge.value && !editTimeInitialized.value)) {
      return;
    }

    chosenStartTime.value = getDefaultStartTime();
  },
);

watch(
  [effectiveDateKey, effectiveTimeZone],
  ([newDateKey, newTz], oldValues) => {
    if (isEditChallenge.value && !editTimeInitialized.value && mapStore.editGambitStart) {
      const tz = newTz;
      const gambitDateKey = format(toZonedTime(new Date(mapStore.editGambitStart), tz), 'yyyy-MM-dd');
      if (newDateKey === gambitDateKey) {
        editTimeInitialized.value = true;
        chosenStartTime.value = clampStartTime(new Date(mapStore.editGambitStart));
        return;
      }
    }

    if (userHasSetTime.value && oldValues) {
      const prevTz = oldValues[1] || newTz;
      const zonedPrev = toZonedTime(chosenStartTime.value, prevTz);
      const wallHours = zonedPrev.getHours();
      const wallMinutes = zonedPrev.getMinutes();
      const wallTimeStr = `${String(wallHours).padStart(2, '0')}:${String(wallMinutes).padStart(2, '0')}`;
      const candidate = new Date(`${String(newDateKey).substring(0, 10)}T${wallTimeStr}:00`);
      if (!Number.isNaN(candidate.getTime())) {
        try {
          chosenStartTime.value = clampStartTime(fromZonedTime(candidate, newTz));
          return;
        } catch {
          // fall through to default
        }
      }
    }

    chosenStartTime.value = getDefaultStartTime();
  },
  { immediate: true },
);

watch(
  availableIntents,
  (nextIntents) => {
    if (!nextIntents.length) {
      selectedIntentId.value = '';
      return;
    }
    if (!selectedIntentId.value || !nextIntents.some((i) => i.id === selectedIntentId.value)) {
      if (isEditChallenge.value && mapStore.editGambitStart) {
        const tz = city.value?.timezone || 'UTC';
        const gambitDateKey = format(toZonedTime(new Date(mapStore.editGambitStart), tz), 'yyyy-MM-dd');
        const match = nextIntents.find((i) => String(i.date).substring(0, 10) === gambitDateKey);
        selectedIntentId.value = match?.id || pickDefaultIntentId();
      } else {
        selectedIntentId.value = pickDefaultIntentId();
      }
    }
  },
  { immediate: true },
);

function handlePreviousTime() {
  if (!isPreviousTimeAvailable.value) return;

  userHasSetTime.value = true;
  const zonedCurrent = toZonedTime(chosenStartTime.value, effectiveTimeZone.value);
  const minuteRemainder = zonedCurrent.getMinutes() % TIME_ARROW_STEP_MINUTES;
  const isOnHalfHour = minuteRemainder === 0 && zonedCurrent.getSeconds() === 0 && zonedCurrent.getMilliseconds() === 0;
  const minutesToSubtract = isOnHalfHour ? TIME_ARROW_STEP_MINUTES : minuteRemainder;

  chosenStartTime.value = clampStartTime(subMinutes(chosenStartTime.value, minutesToSubtract));
}

function handleNextTime() {
  if (!isNextTimeAvailable.value) return;

  userHasSetTime.value = true;
  const zonedCurrent = toZonedTime(chosenStartTime.value, effectiveTimeZone.value);
  const minuteRemainder = zonedCurrent.getMinutes() % TIME_ARROW_STEP_MINUTES;
  const isOnHalfHour = minuteRemainder === 0 && zonedCurrent.getSeconds() === 0 && zonedCurrent.getMilliseconds() === 0;
  const minutesToAdd = isOnHalfHour ? TIME_ARROW_STEP_MINUTES : TIME_ARROW_STEP_MINUTES - minuteRemainder;

  chosenStartTime.value = clampStartTime(addMinutes(chosenStartTime.value, minutesToAdd));
}

onMounted(() => {
  sparkStore.fetchIntents();

  const bar = elementOf(actionBarRef.value);
  const counter = elementOf(chessCounterRef.value);
  if (typeof ResizeObserver !== 'undefined' && bar) {
    inviteResizeObserver = new ResizeObserver(() => measureInviteFaces());
    inviteResizeObserver.observe(bar);
    if (counter) inviteResizeObserver.observe(counter);
  }
  measureInviteFaces();
});

onBeforeUnmount(() => {
  inviteResizeObserver?.disconnect();
});

async function resolveIntentId(resolvedLocationId?: string): Promise<string | null> {
  if (selectedIntent.value) {
    return selectedIntent.value.id;
  }

  if (!city.value?.id) return null;

  const locationId = resolvedLocationId || props.location.id;
  const startTime = chosenStartTime.value;
  const endTime = addHours(startTime, 2);

  const intentResult = await sparkStore.findOrCreateIntent({
    cityId: city.value.id,
    locationIds: [locationId],
    start: startTime.toISOString(),
    end: endTime.toISOString(),
    coordinates: props.location.coordinates,
    radiusMeters: 10000,
    mapZoom: 12,
  });

  return intentResult.data?.id || null;
}

async function resolveLocationId(): Promise<string | null> {
  if (!isVirtualLocation.value) return props.location.id;

  if (resolvedVirtualLocationId.value) return resolvedVirtualLocationId.value;

  const locationProps = {
    name: props.location.name || props.location.address || 'New spot',
    address: props.location.address || '',
    type: LocationType.CHESS_FRIENDLY,
    generalFeatures: [],
    chessFeatures: [],
    coordinates: props.location.coordinates,
    photos: [],
    private: false,
  };

  const result = await handleCreateLocation(locationProps);

  if (!result || typeof result !== 'object' || !('id' in result)) {
    return null;
  }

  resolvedVirtualLocationId.value = (result as MapLocationDto).id;
  return resolvedVirtualLocationId.value;
}

async function handleEditSubmit() {
  const gambitId = mapStore.editGambitId;
  if (!gambitId) return;

  isSubmitting.value = true;

  try {
    const locationId = props.location.id;
    if (!locationId || locationId === mapStore.VIRTUAL_LOCATION_ID) {
      useToast().error('Please select an existing spot.');
      return;
    }

    const startTime = chosenStartTime.value;
    const endTime = addHours(startTime, 2);

    const resolvedGambitType = normalizeProposalGambitTypes(gambitType.value);
    const resolvedChessSetPolicy =
      chessSetCount.value > 0 ? GambitChessSetPolicy.CHESS_SET_ORGANIZER : GambitChessSetPolicy.CHESS_SET_NEEDED;
    const payload: GambitForm = {
      startDate: format(startTime, 'yyyy-MM-dd'),
      startTime: format(startTime, 'HH:mm'),
      endDate: format(endTime, 'yyyy-MM-dd'),
      endTime: format(endTime, 'HH:mm'),
      locationId,
      gambitType: resolvedGambitType,
      gambitCategory: isEditChallenge.value ? GambitCategory.CASUAL : GambitCategory.SPARK,
      chessSetPolicy: resolvedChessSetPolicy,
      streamed: gambitType.value.includes(GambitType.STREAMED),
      timed: gambitType.value.includes(GambitType.TIMED),
      private: isEditChallenge.value,
      clubId: mapStore.proposalClubId ?? null,
      title: title.value || props.defaultTitle || '',
    };

    let error: string | null = null;
    let survivingId = gambitId;

    if (isEditChallenge.value) {
      const result = await gambits.gambitService.updateChallenge(gambitId, payload);
      if (result.error) error = result.error.message || 'Could not update challenge.';
      if (result.data?.gambitId) survivingId = result.data.gambitId;
    } else {
      const result = await gambits.gambitService.updateGambit(gambitId, payload);
      if (result.error) error = result.error.message || 'Could not update IRL.';
    }

    if (error) {
      useToast().error(error);
      return;
    }

    /* The Sparks deck stays alive behind the overlay; without this it shows the
       pre-edit card until the next full reload. */
    sparkStore.gambitChanged(
      gambitId,
      survivingId !== gambitId ? { kind: 'merged', intoGambitId: survivingId } : { kind: 'updated' },
    );
    void sparkStore.findMyGambits(new Date(), addDays(new Date(), 7));

    useToast().success(isEditChallenge.value ? 'Challenge updated.' : 'IRL updated.');
    mapStore.exitProposalMode();
    router.push({ name: RouterNameType.GAMBIT, params: { id: survivingId } });
  } catch {
    useToast().error('Could not save changes. Please try again.');
  } finally {
    isSubmitting.value = false;
  }
}

async function handleCreateProposal() {
  if (isEditMode.value) {
    return handleEditSubmit();
  }

  if (!city.value?.id || !hasAvailableStartTimes.value) return;

  isSubmitting.value = true;

  try {
    if (!isVirtualLocation.value) {
      const proceed = await confirmClosedSpotIfOutside({
        locationId: props.location.id,
        openingHours: props.openingHours,
        start: chosenStartTime.value,
        timeZone: effectiveTimeZone.value,
      });
      if (!proceed) return;
    }

    const locationId = await resolveLocationId();
    if (!locationId) {
      useToast().error('Could not create location. Please try again.');
      return;
    }

    const startTime = chosenStartTime.value;
    const endTime = addHours(startTime, 2);

    const resolvedGambitType = normalizeProposalGambitTypes(gambitType.value);
    const resolvedCategory = props.isChallenge ? GambitCategory.CASUAL : GambitCategory.SPARK;
    const resolvedChessSetPolicy =
      chessSetCount.value > 0 ? GambitChessSetPolicy.CHESS_SET_ORGANIZER : GambitChessSetPolicy.CHESS_SET_NEEDED;
    const payload: GambitForm = {
      startDate: format(startTime, 'yyyy-MM-dd'),
      startTime: format(startTime, 'HH:mm'),
      endDate: format(endTime, 'yyyy-MM-dd'),
      endTime: format(endTime, 'HH:mm'),
      locationId,
      gambitType: resolvedGambitType,
      gambitCategory: resolvedCategory,
      chessSetPolicy: resolvedChessSetPolicy,
      ownerChessSetCount: chessSetCount.value,
      streamed: gambitType.value.includes(GambitType.STREAMED),
      timed: gambitType.value.includes(GambitType.TIMED),
      private: props.isChallenge ? true : false,
      clubId: mapStore.proposalClubId ?? null,
      title: title.value || props.defaultTitle || '',
    };

    if (props.isChallenge && props.challengeUserId) {
      payload.isChallenge = true;
      payload.invitationUserIds = [props.challengeUserId];
      const result = await gambits.gambitService.addNewGambit(payload);

      if (result.error) {
        if (result.error.gambitId) {
          pendingGambitConflictId = result.error.gambitId;
          await openGambitExistsModal();
          return;
        }
        useToast().error(result.error.message || 'Could not create challenge. Please try again.');
        return;
      }

      useToast().success('Challenge created.');
      emit('created', locationId);
      if (result.data?.id) {
        router.push({ name: RouterNameType.GAMBIT, params: { id: result.data.id }, query: { back: 'history' } });
      }
      return;
    }

    const intentId = await resolveIntentId(locationId);

    if (!intentId) {
      useToast().error('Could not create proposal. Please try again.');
      return;
    }

    const startDate = new Date(`${payload.startDate}T${payload.startTime}`);
    const endDate = new Date(`${payload.endDate}T${payload.endTime}`);

    const proposalPayload: CreateProposalDto = {
      start: format(startDate, "yyyy-MM-dd'T'HH:mm:ss.SSS"),
      end: format(endDate, "yyyy-MM-dd'T'HH:mm:ss.SSS"),
      locationId,
      intentId,
      gambitType: normalizeProposalGambitTypes(payload.gambitType),
      ownerChessSetCount: payload.ownerChessSetCount ?? 0,
      title: payload.title || undefined,
    };

    const response = await gambits.sparkService.createProposal(proposalPayload);

    if (response.error) {
      if (response.error.gambitId) {
        pendingGambitConflictId = response.error.gambitId;
        await openGambitExistsModal();
        return;
      }
      useToast().error(response.error.message || 'Could not create proposal. Please try again.');
      return;
    }

    if (response.data?.id && selectedPlayers.value.length > 0) {
      const participantIds = selectedPlayers.value.map((p) => p.id);
      await gambits.sparkService.createInvitations(response.data.id, participantIds).catch(() => {});
      selectedPlayers.value = [];
    }

    useToast().success('Proposal created.');
    emit('created', locationId);
  } catch {
    useToast().error('Could not create proposal. Please try again.');
  } finally {
    isSubmitting.value = false;
  }
}

watch(
  () => props.defaultTitle,
  (newDefault, oldDefault) => {
    if (newDefault && (!title.value || title.value === oldDefault)) {
      title.value = newDefault;
    }
  },
);

defineExpose({ chosenStartTime, effectiveTimeZone });
</script>

<style lang="scss" scoped>
@use '@/ui/css/mixins' as *;

.MapProposalCard {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  padding: var(--spacing-xl) var(--spacing-xl) 0;
  background: var(--color-dark-700);
  border-radius: var(--radius-large);
  border: 1px solid var(--color-white-10);
  position: relative;
  overflow: visible;
  box-shadow: 0px 0px var(--radius-normal) var(--color-dark-900);

  &.embedded {
    border-radius: var(--radius-large) var(--radius-large) 0 0;
    box-shadow: none;
    border: none;
  }
}

.bottom-ctas {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 0.75rem 1.5rem var(--safe-bottom-offset);
  width: 100%;
}

.proposal-cta-circle {
  width: 3.5rem;
  height: 3.5rem;
  border-radius: 3.5rem;
  border: 1px solid var(--color-white-10);
  backdrop-filter: blur(8px);
  background-color: var(--color-dark-5);
  color: var(--color-white-70);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 200ms linear;

  @include hover {
    background-color: var(--color-dark-20);
    border-color: var(--color-white-30);
    color: var(--color-white);
  }

  // The breathing variant animates `transform`, so its animation wins here.
  @include press;

  &.proposal-cta-play {
    background: var(--color-linear-gradient-30), rgba(0, 0, 0, 1);
    border-color: transparent;
    color: var(--color-white);
    box-shadow: var(--shadow-button-action);

    @include hover {
      background: var(--color-linear-gradient-30), rgba(255, 255, 255, 0.25);
    }

    &.is-disabled {
      opacity: 0.5;
      pointer-events: none;
      filter: saturate(0.75);
    }
  }
}

.location-label {
  display: flex;
  align-items: center;
  color: var(--color-white-80);
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 0.03em;
  max-width: 100%;
  overflow: hidden;
}

.location-label .label-text {
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
}

.time-span {
  text-transform: uppercase;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;
}

.time-span .hours {
  font-size: 48px;
  font-weight: 200;
  letter-spacing: -0.02em;
  color: var(--color-white);
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.time-span .time-display {
  background: transparent;
  color: inherit;
  font: inherit;
  letter-spacing: inherit;
  text-align: center;
  width: 3em;
  padding: 0;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.time-span .time-display[aria-disabled='true'] {
  opacity: 0.5;
}

.time-span .time-display.visual-left-align {
  transform: translateX(-5px);
}

.context-label {
  font-size: 12px;
  font-weight: 500;
  color: var(--color-white-50);
  text-align: center;
  letter-spacing: 0.05em;
  margin-bottom: 0.5rem;
}

.intent-day {
  margin-top: 0.25rem;
}

.inline-options {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  justify-content: flex-start;
  flex-wrap: wrap;
}

.option-pill {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  height: 30px;
  padding: 0 0.75rem;
  border-radius: 30px;
  border: 1px solid var(--color-white-10);
  backdrop-filter: blur(8px);
  background-color: var(--color-dark-5);
  color: var(--color-white-70);
  cursor: pointer;
  transition: all 200ms linear;
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.03em;
  white-space: nowrap;
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;

  @include hover {
    background-color: var(--color-dark-20);
    border-color: var(--color-white-30);
    color: var(--color-white);
  }

  &.active {
    border-color: var(--color-pink);
    color: var(--color-pink);
  }
}

.inline-title-input {
  width: 100%;
}

.inline-tags {
  width: 100%;
}

.options-expanded {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;
}

.action-bar {
  width: 100%;
  border-radius: 0;
  border-top: 1px solid var(--color-white-20);
  background: transparent;
  padding: 1rem 0 0.5rem !important;
  margin-top: 0.5rem;
}

.expand-enter-active,
.expand-leave-active {
  transition: all 200ms ease;
  overflow: hidden;
}

.expand-enter-from,
.expand-leave-to {
  opacity: 0;
  max-height: 0;
}

.expand-enter-to,
.expand-leave-from {
  opacity: 1;
  max-height: 300px;
}

.is-breathing {
  animation: breathe 4s ease-in-out infinite;
}

@keyframes breathe {
  0%,
  100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 oklch(0.72 0.19 var(--hue, 0) / 0.4);
  }
  50% {
    transform: scale(1.06);
    box-shadow: 0 0 12px 4px oklch(0.72 0.19 var(--hue, 0) / 0.25);
  }
}

.player-circles {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
  --card-avatar-size: 40px;
  --card-badge-size: 14px;
}

.player-circle {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
}

.invite-count {
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.03em;
  line-height: 1;
}

.circle-placeholder {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 1px dashed var(--color-white-10);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-white-30);
  font-size: 12px;

  &.invite {
    cursor: pointer;
    border-color: var(--color-white-30);
    transition: all 200ms ease;

    @include hover {
      border-color: var(--color-pink);
      color: var(--color-pink);
    }
  }
}
</style>
